import React from 'react';
import { ProjectFeedWithLoading } from './ProjectFeedWithLoading';
import { TriageFilterBar } from '@components/molecules/TriageFilterBar';
import { WarehouseAlertsButton } from '@components/organisms/WarehouseAlertsButton';
import {
  TriageFilter,
  TriageCounts,
} from '@components/molecules/TriageFilterBar/TriageFilterBar.types';

interface DashboardContentProps {
  // Project Feed
  activities: any[];
  paginationActions: any;
  totalCount: number;
  isActivitiesLoading?: boolean;
  onOpenTaskDetail: (taskId: string) => void;
  onProjectClick?: (projectId: string) => void;
  filterSlot?: React.ReactNode;
  /** Sprint 16, story-075/076. Rendered as the first child, above `filterSlot` — the "Needs your
   * action" block sits outside the feed's own scroll area, so it never scrolls away. */
  needsYourActionSlot?: React.ReactNode;

  // Triage filter (dashboard only) — replaces the old pending-* summary sections
  triageFilter: TriageFilter;
  onTriageChange: (filter: TriageFilter) => void;
  triageCounts: TriageCounts;
  canInvoice?: boolean;
}

export const DashboardContent: React.FC<DashboardContentProps> = ({
  activities,
  paginationActions,
  totalCount,
  isActivitiesLoading = false,
  onOpenTaskDetail,
  onProjectClick,
  filterSlot,
  needsYourActionSlot,
  triageFilter,
  onTriageChange,
  triageCounts,
  canInvoice = true,
}) => {
  return (
    // Fill the remaining column height as a flex column: the filter + pills stay
    // fixed, and only the feed scrolls internally (min-h-0 lets the scroll area
    // shrink instead of pushing the column taller).
    <div className="flex flex-col flex-1 min-h-0 space-y-4">
      {/* Needs your action — above the feed and above filterSlot, outside the feed's own
          overflow-y-auto scroll area, so it never scrolls away. */}
      {needsYourActionSlot}

      {/* Filter + Refresh */}
      {filterSlot}

      {/* Triage filter pills (counts from dedicated queries; hidden when 0).
          The warehouse alerts pill rides at the end of the same row and opens
          its own slide-over instead of filtering the feed. */}
      <TriageFilterBar
        activeFilter={triageFilter}
        onChange={onTriageChange}
        counts={triageCounts}
        canInvoice={canInvoice}
        trailingSlot={<WarehouseAlertsButton />}
      />

      {/* Project Feed — the only part of the column that scrolls (invisible
          scrollbar, mirroring the Active Projects rail). */}
      <div className="mt-6 flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <ProjectFeedWithLoading
          activities={activities}
          totalCount={totalCount}
          isLoading={isActivitiesLoading}
          paginationActions={paginationActions}
          handleOpenTaskDetail={onOpenTaskDetail}
          onProjectClick={onProjectClick}
        />
      </div>
    </div>
  );
};
